import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { userApi, adminApi, UserRole, Permission, UserInfo, ALL_ROLES } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { getPermissionsForRole } from '../utils/permissions';

interface RoleContextType {
  role: UserRole | null;
  actualRole: UserRole | null; // The user's real role (before impersonation)
  permissions: Record<Permission, boolean> | null;
  loading: boolean;
  error: string | null;
  can: (permission: Permission) => boolean;
  isRole: (role: UserRole) => boolean;
  canImpersonate: boolean;
  impersonatedRole: UserRole | null;
  setImpersonatedRole: (role: UserRole | null) => void;
  availableRoles: UserRole[];
  refetch: () => Promise<void>;
}

const RoleContext = createContext<RoleContextType | undefined>(undefined);

const defaultPermissions: Record<Permission, boolean> = {
  'dashboard:view': false,
  'dashboard:edit': false,
  'leads:view': false,
  'leads:view_assignments': false,
  'leads:claim': false,
  'leads:update_status': false,
  'leads:assign': false,
  'leads:approve_request': false,
  'leads:return_to_pool': false,
  'sources:view': false,
  'sources:fetch': false,
  'admin:impersonate': false,
  'admin:manage_users': false,
  'automation:view': false,
  'automation:configure': false,
};

export function RoleProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonatedRole, setImpersonatedRole] = useState<UserRole | null>(null);
  const [permissionsMatrix, setPermissionsMatrix] = useState<Record<UserRole, Record<Permission, boolean>> | null>(null);

  const fetchUserInfo = useCallback(async () => {
    if (!isAuthenticated) {
      setUserInfo(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const info = await userApi.getMe();
      setUserInfo(info);

      // If superadmin, fetch permissions matrix for impersonation
      if (info.role === 'superadmin') {
        try {
          const { matrix } = await adminApi.getPermissions();
          setPermissionsMatrix(matrix);
        } catch {
          // Ignore - will fall back to hardcoded permissions
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch user role';
      setError(errorMessage);
      setUserInfo(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authLoading) {
      fetchUserInfo();
    }
  }, [authLoading, fetchUserInfo]);

  // Determine if user can impersonate (superadmin only)
  const canImpersonate = userInfo?.role === 'superadmin';

  // Get effective role (impersonated or actual)
  const effectiveRole = impersonatedRole ?? userInfo?.role ?? null;

  // Get effective permissions based on impersonated role or actual permissions
  // Use server-side matrix if available (has database overrides), otherwise fall back to hardcoded
  const effectivePermissions = impersonatedRole
    ? (permissionsMatrix?.[impersonatedRole] ?? getPermissionsForRole(impersonatedRole))
    : userInfo?.permissions ?? defaultPermissions;

  const can = useCallback(
    (permission: Permission): boolean => {
      return effectivePermissions[permission] ?? false;
    },
    [effectivePermissions]
  );

  const isRole = useCallback(
    (role: UserRole): boolean => {
      return effectiveRole === role;
    },
    [effectiveRole]
  );

  const handleSetImpersonatedRole = useCallback(
    (role: UserRole | null) => {
      if (!canImpersonate) return;
      setImpersonatedRole(role);
    },
    [canImpersonate]
  );

  return (
    <RoleContext.Provider
      value={{
        role: effectiveRole,
        actualRole: userInfo?.role ?? null,
        permissions: effectivePermissions,
        loading: authLoading || loading,
        error,
        can,
        isRole,
        canImpersonate,
        impersonatedRole,
        setImpersonatedRole: handleSetImpersonatedRole,
        availableRoles: ALL_ROLES,
        refetch: fetchUserInfo,
      }}
    >
      {children}
    </RoleContext.Provider>
  );
}

export function useRole() {
  const context = useContext(RoleContext);
  if (context === undefined) {
    throw new Error('useRole must be used within a RoleProvider');
  }
  return context;
}
